import { BiddingUtils } from '@/vb-utils/bidding.util';
import { HttpErrorResponse } from '@angular/common/http';
import {
    Component,
    EventEmitter,
    Inject,
    Input,
    NgZone,
    OnDestroy,
    OnInit,
    Output,
} from '@angular/core';
import {
    AbstractControl,
    FormBuilder,
    FormControl,
    FormGroup,
    ValidationErrors,
    Validators,
} from '@angular/forms';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import {
    AccountAddress,
    AccountPreferenceByAccountMappingsId200Response,
    AccountPreferences,
    AddCardRequest,
    AuctionCatalogsService,
    GetAllAddresses200Response,
    ProfilesService,
    UsersService,
} from '@viewbid/ts-angular-client';
import { VbSnackbarService } from '@viewbid/ui';
import { get } from 'scriptjs';
import { CardTypes } from '../../enums/card-types';
import { PaymentErrorCodes } from '../../enums/payment-error-codes';
import { Store } from '@ngrx/store';
import { AppState } from 'src/app/viewbid/state/app-state';
import { selectUser } from 'src/app/viewbid/state/app-selectors';
import { User } from 'viewbid-shared-lib';
import { Subject, take, takeUntil } from 'rxjs';
import { ApplicationConfiguration } from 'src/app/viewbid/shared/services/app.config';
import { ProfileService as UserProfileService } from 'src/app/viewbid/shared/services/profile.service';

interface AcceptJsMessage {
    code: string;
    text: string;
}

interface AcceptJsResponse {
    messages: {
        resultCode: string;
        message: AcceptJsMessage[];
    };
    opaqueData?: {
        dataDescriptor: string;
        dataValue: string;
    };
}

interface AcceptJsStatic {
    dispatchData(
        request: {
            authData: { apiLoginID: string; clientKey: string };
            cardData: {
                cardNumber: string;
                month: string;
                year: string;
                cardCode: string;
                fullName: string;
            };
        },
        callback: (response: AcceptJsResponse) => void,
    ): void;
}

@Component({
    selector: 'vb-add-payment-information-form',
    templateUrl: './add-payment-information-form.component.html',
    styleUrls: ['./add-payment-information-form.component.scss'],
})
export class AddPaymentInformationFormComponent implements OnInit, OnDestroy {
    userAddresses: AccountAddress[] = [];
    defaultAddresses: string = '';
    selectedShippingAddress: AccountAddress | null = null;
    private successToastId: number = 0;
    cardForm!: FormGroup;
    formSubmitted: boolean = false;
    isSubmitting: boolean = false;
    isLoading: boolean = false;
    detectedBrand: string = 'unknown';
    apiLoginId: string | null = null;
    clientKey: string | null = null;
    private acceptJsReady: boolean = false;
    private previousExpiryValue: string = '';
    private readonly destroy$: Subject<void> = new Subject<void>();
    @Input() isFromRegistration: boolean = false;
    @Output() isCardAdded: EventEmitter<boolean> = new EventEmitter<boolean>();

    constructor(
        @Inject('window') private window: any,
        private userService: UsersService,
        private profileService: ProfilesService,
        private userProfileService: UserProfileService,
        public snackbarService: VbSnackbarService,
        private translate: TranslateService,
        private router: Router,
        private store: Store<AppState>,
        private biddingUtils: BiddingUtils,
        private formBuilder: FormBuilder,
        private auctionCatalogsService: AuctionCatalogsService,
        private applicationConfiguration: ApplicationConfiguration,
        private ngZone: NgZone,
    ) {
        this.apiLoginId = this.getAuthorizeApiLoginId();
        this.clientKey = this.getAuthorizeClientKey();
    }

    ngOnInit(): void {
        this.cardForm = this.formBuilder.group({
            firstName: new FormControl('', [
                Validators.required,
                Validators.maxLength(50),
            ]),
            lastName: new FormControl('', [
                Validators.required,
                Validators.maxLength(50),
            ]),
            cardNumber: new FormControl('', [
                Validators.required,
                this.cardNumberValidator,
            ]),
            expiry: new FormControl('', [
                Validators.required,
                this.expiryValidator,
            ]),
            cvv: new FormControl('', [Validators.required, this.cvvValidator]),
        });

        // Amex takes a 4-digit code, everything else 3 — the CVV validator
        // reads the card number, so it must re-run when the number changes.
        this.cardForm
            .get('cardNumber')
            ?.valueChanges.pipe(takeUntil(this.destroy$))
            .subscribe(() => {
                this.cardForm
                    .get('cvv')
                    ?.updateValueAndValidity({ emitEvent: false });
            });

        this.prefillCardholderName();
        this.loadAcceptJs();

        this.biddingUtils
            .getCurrentUserId()
            .pipe(takeUntil(this.destroy$))
            .subscribe(async (currentUserId: string) => {
                await this.getUserPreferences(currentUserId);
                await this.getAllAddresses();
            });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    getAuthorizeURL(): string {
        return this.applicationConfiguration.configuration[
            'authorize_uri'
        ] as string;
    }

    getAuthorizeApiLoginId(): string {
        return this.applicationConfiguration.configuration[
            'authorize_api_login_id'
        ] as string;
    }

    getAuthorizeClientKey(): string {
        return this.applicationConfiguration.configuration[
            'authorize_client_key'
        ] as string;
    }

    private prefillCardholderName(): void {
        this.store
            .select(selectUser)
            .pipe(take(1))
            .subscribe((user: User | null) => {
                if (!user) {
                    return;
                }
                const firstName: AbstractControl<any, any> | null =
                    this.cardForm.get('firstName');
                const lastName: AbstractControl<any, any> | null =
                    this.cardForm.get('lastName');
                if (firstName && !firstName.value && user.firstname) {
                    firstName.setValue(user.firstname);
                }
                if (lastName && !lastName.value && user.lastname) {
                    lastName.setValue(user.lastname);
                }
            });
    }

    private loadAcceptJs(): void {
        try {
            get(this.getAuthorizeURL(), () => {
                this.ngZone.run(() => {
                    this.acceptJsReady = true;
                });
            });
        } catch {
            this.acceptJsReady = false;
        }
    }

    private getAllAddresses(): void {
        this.isLoading = true;
        this.userService.getAllAddresses(1, -1).subscribe({
            next: (response: GetAllAddresses200Response) => {
                this.isLoading = false;
                this.userAddresses = response.data;
            },
            error: () => {
                this.isLoading = false;
                this.snackbarService.error(
                    this.translate.instant('Invoices.FetchAddressError'),
                );
            },
        });
    }

    private async getUserPreferences(userId: string): Promise<void> {
        this.isLoading = true;
        if (userId) {
            await this.profileService
                .accountPreferenceByAccountMappingsId(userId)
                .subscribe({
                    next: (
                        response: AccountPreferenceByAccountMappingsId200Response,
                    ) => {
                        this.isLoading = false;
                        const preferenceResponse: AccountPreferences =
                            response.data as AccountPreferences;
                        this.defaultAddresses =
                            preferenceResponse.preferences?.defaultAddressId ??
                            '';
                    },
                    error: () => {
                        this.isLoading = false;
                        this.snackbarService.error(
                            this.translate.instant(
                                'Invoices.FetchUserPreferenceError',
                            ),
                        );
                    },
                });
        }
    }

    goToAddAddress(): void {
        this.router.navigate(['profiles/my-profile/user-addresses']);
    }

    getCardType(bin: string): string {
        if (/^4/.test(bin)) {
            return CardTypes.Visa;
        } else if (/^5[1-5]/.test(bin)) {
            return CardTypes.MasterCard;
        } else if (/^(34|37)/.test(bin)) {
            return CardTypes.Amex;
        } else if (/^6011|^(622[1-9][2-5]|64[4-9]|65)/.test(bin)) {
            return CardTypes.Discover;
        } else if (/^35(2[8-9]|[3-8][0-9])/.test(bin)) {
            return CardTypes.JCB;
        } else if (/^3(0[0-5]|[2-9][0-9]|[6-9])/.test(bin)) {
            return CardTypes.DinersClub;
        } else {
            return 'unknown';
        }
    }

    get brandLabel(): string | null {
        switch (this.detectedBrand) {
            case CardTypes.Visa:
                return 'VISA';
            case CardTypes.MasterCard:
                return 'MASTERCARD';
            case CardTypes.Amex:
                return 'AMEX';
            case CardTypes.Discover:
                return 'DISCOVER';
            case CardTypes.JCB:
                return 'JCB';
            case CardTypes.DinersClub:
                return 'DINERS';
            default:
                return null;
        }
    }

    /** Exactly 16 digits (15 for Amex) + Luhn. Required is handled separately. */
    private cardNumberValidator = (
        control: AbstractControl,
    ): ValidationErrors | null => {
        const digits: string = ((control.value as string) ?? '').replace(
            /\D/g,
            '',
        );
        if (!digits) {
            return null;
        }
        const requiredLength: number =
            this.getCardType(digits) === CardTypes.Amex ? 15 : 16;
        if (digits.length !== requiredLength) {
            return { cardNumberLength: { requiredLength } };
        }
        let sum: number = 0;
        let double: boolean = false;
        for (let i: number = digits.length - 1; i >= 0; i--) {
            let digit: number = parseInt(digits[i], 10);
            if (double) {
                digit *= 2;
                if (digit > 9) {
                    digit -= 9;
                }
            }
            sum += digit;
            double = !double;
        }
        return sum % 10 === 0 ? null : { cardNumber: true };
    };

    /** MM/YY format, not in the past (the current month is still valid). */
    private expiryValidator = (
        control: AbstractControl,
    ): ValidationErrors | null => {
        const value: string = (control.value as string) ?? '';
        if (!value) {
            return null;
        }
        if (!/^(0[1-9]|1[0-2])\/\d{2}$/.test(value)) {
            return { expiryFormat: true };
        }
        const month: number = parseInt(value.slice(0, 2), 10);
        const year: number = 2000 + parseInt(value.slice(3), 10);
        const now: Date = new Date();
        if (
            year < now.getFullYear() ||
            (year === now.getFullYear() && month < now.getMonth() + 1)
        ) {
            return { expiryPast: true };
        }
        return null;
    };

    /** 4 digits when the detected brand is Amex, else 3. */
    private cvvValidator = (
        control: AbstractControl,
    ): ValidationErrors | null => {
        const value: string = (control.value as string) ?? '';
        if (!value) {
            return null;
        }
        const digits: string = (
            (this.cardForm?.get('cardNumber')?.value as string) ?? ''
        ).replace(/\D/g, '');
        const requiredLength: number =
            this.getCardType(digits) === CardTypes.Amex ? 4 : 3;
        return new RegExp(`^\\d{${requiredLength}}$`).test(value)
            ? null
            : { cvvLength: true };
    };

    onCardNumberInput(event: Event): void {
        const input: HTMLInputElement = event.target as HTMLInputElement;
        const caret: number = input.selectionStart ?? input.value.length;
        const digitsBeforeCaret: number = input.value
            .slice(0, caret)
            .replace(/\D/g, '').length;
        const rawDigits: string = input.value.replace(/\D/g, '');
        // Amex numbers are 15 digits; everything else accepted here is 16.
        const maxDigits: number =
            this.getCardType(rawDigits) === CardTypes.Amex ? 15 : 16;
        const digits: string = rawDigits.slice(0, maxDigits);
        const formatted: string = digits.replace(/(\d{4})(?=\d)/g, '$1 ');
        this.detectedBrand = this.getCardType(digits);
        this.cardForm.get('cardNumber')?.setValue(formatted);
        // setValue rewrites the input and drops the caret to the end; restore
        // it by digit index so mid-string edits don't jump.
        let newCaret: number = 0;
        let digitsSeen: number = 0;
        while (newCaret < formatted.length && digitsSeen < digitsBeforeCaret) {
            if (/\d/.test(formatted[newCaret])) {
                digitsSeen++;
            }
            newCaret++;
        }
        input.setSelectionRange(newCaret, newCaret);
    }

    onExpiryInput(event: Event): void {
        const input: HTMLInputElement = event.target as HTMLInputElement;
        const digits: string = input.value.replace(/\D/g, '').slice(0, 4);
        const grew: boolean =
            input.value.length > this.previousExpiryValue.length;
        let formatted: string;
        if (digits.length > 2) {
            formatted = `${digits.slice(0, 2)}/${digits.slice(2)}`;
        } else if (digits.length === 2 && grew) {
            // Auto-insert the slash only while typing forward so backspacing
            // through it doesn't fight the user.
            formatted = `${digits}/`;
        } else {
            formatted = digits;
        }
        this.cardForm.get('expiry')?.setValue(formatted);
        this.previousExpiryValue = formatted;
    }

    onCvvInput(event: Event): void {
        const input: HTMLInputElement = event.target as HTMLInputElement;
        const digits: string = input.value.replace(/\D/g, '').slice(0, 4);
        this.cardForm.get('cvv')?.setValue(digits);
    }

    getHelperText(field: string): string | null {
        const control: AbstractControl<any, any> | null =
            this.cardForm.get(field);
        if (!control || !control.errors) {
            return null;
        }
        if (!(control.touched || this.formSubmitted)) {
            return null;
        }
        if (control.errors['acceptjs']) {
            return control.errors['acceptjs'] as string;
        }
        switch (field) {
            case 'firstName':
                if (control.errors['required']) {
                    return 'First name is required';
                }
                if (control.errors['maxlength']) {
                    return `First name can be max ${control.errors['maxlength'].requiredLength} characters long`;
                }
                break;
            case 'lastName':
                if (control.errors['required']) {
                    return 'Last name is required';
                }
                if (control.errors['maxlength']) {
                    return `Last name can be max ${control.errors['maxlength'].requiredLength} characters long`;
                }
                break;
            case 'cardNumber':
                if (control.errors['required']) {
                    return 'Card number is required';
                }
                if (control.errors['cardNumberLength']) {
                    return `Card number must be ${control.errors['cardNumberLength'].requiredLength} digits`;
                }
                if (control.errors['cardNumber']) {
                    return 'Enter a valid card number';
                }
                break;
            case 'expiry':
                if (control.errors['required']) {
                    return 'Expiry date is required';
                }
                if (control.errors['expiryFormat']) {
                    return 'Enter a valid expiry date (MM/YY)';
                }
                if (control.errors['expiryPast']) {
                    return 'This card has expired';
                }
                break;
            case 'cvv':
                if (control.errors['required']) {
                    return 'Security code is required';
                }
                if (control.errors['cvvLength']) {
                    return 'Enter a valid security code';
                }
                break;
            default:
                return null;
        }
        return null;
    }

    onSubmit(): void {
        this.formSubmitted = true;
        if (this.cardForm.invalid || !this.selectedShippingAddress) {
            this.cardForm.markAllAsTouched();
            return;
        }
        if (this.isSubmitting) {
            return;
        }
        const accept: AcceptJsStatic | undefined = this.window.Accept;
        if (!this.acceptJsReady || !accept?.dispatchData) {
            // Script never loaded (blocked network or missing config) —
            // retry the load once and surface the generic failure toast.
            this.acceptJsReady = false;
            this.loadAcceptJs();
            this.snackbarService.error(
                this.translate.instant('Payments.cardAddFailed'),
            );
            return;
        }
        this.isSubmitting = true;
        const cardDigits: string = (
            (this.cardForm.value.cardNumber as string) ?? ''
        ).replace(/\D/g, '');
        const expiry: string = this.cardForm.value.expiry as string;
        const month: string = expiry.slice(0, 2);
        const year: string = expiry.slice(3);
        const firstName: string = (
            this.cardForm.value.firstName as string
        ).trim();
        const lastName: string = (
            this.cardForm.value.lastName as string
        ).trim();
        // AVS billing is built server-side from addressId — no zip here. The
        // card keeps its own copy of that address, so deleting or editing the
        // saved address later doesn't affect the card.
        accept.dispatchData(
            {
                authData: {
                    apiLoginID: this.apiLoginId ?? '',
                    clientKey: this.clientKey ?? '',
                },
                cardData: {
                    cardNumber: cardDigits,
                    month,
                    year: `20${year}`,
                    cardCode: this.cardForm.value.cvv as string,
                    fullName: `${firstName} ${lastName}`,
                },
            },
            (response: AcceptJsResponse) =>
                // The Accept.js callback fires outside Angular's zone.
                this.ngZone.run(() =>
                    this.handleAcceptResponse(
                        response,
                        cardDigits,
                        `${month}/${year}`,
                    ),
                ),
        );
    }

    private handleAcceptResponse(
        response: AcceptJsResponse,
        cardDigits: string,
        expDate: string,
    ): void {
        if (response.messages.resultCode === 'Ok' && response.opaqueData) {
            this.addPaymentInformation(
                this.buildAddCardRequest(response, cardDigits, expDate),
            );
            return;
        }
        this.isSubmitting = false;
        this.applyAcceptJsErrors(response.messages.message ?? []);
    }

    private applyAcceptJsErrors(messages: AcceptJsMessage[]): void {
        let hasFieldError: boolean = false;
        for (const message of messages) {
            switch (message.code) {
                case 'E_WC_05':
                    this.setFieldError(
                        'cardNumber',
                        'Enter a valid card number',
                    );
                    hasFieldError = true;
                    break;
                case 'E_WC_06':
                case 'E_WC_07':
                    this.setFieldError(
                        'expiry',
                        'Enter a valid expiry date (MM/YY)',
                    );
                    hasFieldError = true;
                    break;
                case 'E_WC_08':
                    this.setFieldError('expiry', 'This card has expired');
                    hasFieldError = true;
                    break;
                case 'E_WC_15':
                    this.setFieldError('cvv', 'Enter a valid security code');
                    hasFieldError = true;
                    break;
                default:
                    // Includes E_WC_21 (bad credentials/config) — never echo
                    // raw Accept.js text for configuration errors.
                    break;
            }
        }
        if (!hasFieldError) {
            this.snackbarService.error(
                this.translate.instant('Payments.cardAddFailed'),
            );
        }
    }

    private setFieldError(field: string, text: string): void {
        this.cardForm.get(field)?.setErrors({ acceptjs: text });
    }

    private buildAddCardRequest(
        response: AcceptJsResponse,
        cardDigits: string,
        expDate: string,
    ): AddCardRequest {
        return {
            customerInformation: {
                firstName: (this.cardForm.value.firstName as string).trim(),
                lastName: (this.cardForm.value.lastName as string).trim(),
            },
            // Masked values matching AcceptUI's shape — the server validates
            // this block, then drops it. Only opaqueData reaches Authorize.Net.
            encryptedCardData: {
                cardNumber: `XXXX${cardDigits.slice(-4)}`,
                expDate,
                bin: cardDigits.slice(0, 6),
            },
            messages: {
                resultCode: response.messages.resultCode,
                message: response.messages.message.map(
                    ({ code, text }: AcceptJsMessage) => ({
                        code,
                        text,
                    }),
                ),
            },
            opaqueData: {
                dataDescriptor: response.opaqueData!.dataDescriptor,
                dataValue: response.opaqueData!.dataValue,
            },
            addressId: this.selectedShippingAddress?.id ?? '',
        };
    }

    addPaymentInformation(request: AddCardRequest): void {
        this.auctionCatalogsService.createCard(request).subscribe({
            next: () => {
                if (!this.isFromRegistration) {
                    this.snackbarService.dismiss(this.successToastId);
                    this.successToastId = this.snackbarService.success(
                        this.translate.instant('Payments.cardAddSuccess'),
                        { duration: 15000 },
                    );
                }
                this.isSubmitting = false;
                this.userProfileService.updateUserStore();
                this.formSubmitted = false;
                this.cardForm.reset();
                this.detectedBrand = 'unknown';
                this.previousExpiryValue = '';
                this.goToCardList();
            },
            error: (error: HttpErrorResponse) => {
                const errorMessage: string =
                    error.error.errorCode === PaymentErrorCodes.AVS_MISMATCH &&
                    error.error.details?.errors?.length > 0
                        ? error.error.details.errors[0]
                        : error.error.message;

                this.snackbarService.error(errorMessage, { duration: 15000 });
                this.isSubmitting = false;
                // Keep the address and card fields — for an AVS mismatch the
                // fix is picking another address. Only the CVV must re-enter.
                this.cardForm.get('cvv')?.reset('');
            },
        });
    }

    goToCardList(): void {
        if (this.isFromRegistration) {
            this.isCardAdded.emit(true);
        } else {
            this.router.navigate(['profiles/my-profile/payment-information'], {
                replaceUrl: true,
            });
        }
    }
}
